import React, { useState, useEffect } from 'react';
import {
  Table, Button, Space, Tag, Spin, message, Typography, 
  Tooltip, Empty, Card, Popconfirm,
} from 'antd';
import { 
  FileTextOutlined, EyeOutlined, ReloadOutlined,
  PrinterOutlined, DownloadOutlined, DeleteOutlined, PlusOutlined,
} from '@ant-design/icons';
import { getVouchersByCase, createSettlementPaymentVoucher, deleteVoucher, fetchSettlementPayments } from '../api';
import VoucherDetailsModal from './VoucherDetailsModal';
import { SplitReferenceCell, resolveSplits } from '../../../components/PaymentSplitCells';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const VoucherTable = ({
  caseId,
  caseData, // Pass case data to check case model
  refreshTrigger, // Parent can increment this to force refresh
  onVoucherUpdate,
  isWalkInCase = false, // Pass true if it's a WalkIn case
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  
  // State
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [generatingVoucher, setGeneratingVoucher] = useState(false);
  const [deletingVoucherId, setDeletingVoucherId] = useState(null);

  // Load vouchers
  const loadVouchers = async () => {
    if (!caseId) return;
    
    setLoading(true);
    try {
      const result = await getVouchersByCase(caseId, { limit: 50 });
      setVouchers(result.data?.vouchers || []);
    } catch (error) {
      // 404 is expected if no vouchers exist
      if (error?.response?.status !== 404) {
        console.error('Load vouchers error:', error);
        messageApi.error('Failed to load vouchers');
      } else {
        setVouchers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVouchers();
  }, [caseId, refreshTrigger]);

  // Handle view voucher
  const handleViewVoucher = (voucherId) => {
    setSelectedVoucherId(voucherId);
    setDetailsModalOpen(true);
  };

  // Handle regeneration success
  const handleRegenerateSuccess = (updatedVoucher) => {
    // Refresh vouchers list
    loadVouchers();
    
    // Notify parent
    onVoucherUpdate?.(updatedVoucher);
  };

  // Handle manual voucher generation for WalkIn case (when settlement payment exists but no voucher)
  const handleGenerateVoucher = async () => {
    if (!caseId) {
      messageApi.warning('Case ID not found');
      return;
    }

    setGeneratingVoucher(true);
    try {
      // Fetch latest settlement payment for this case
      const paymentsRes = await fetchSettlementPayments(caseId, { page: 1, limit: 50 });
      const payments = paymentsRes?.data || [];
      
      if (payments.length === 0) {
        messageApi.warning('No settlement payment found for this case');
        setGeneratingVoucher(false);
        return;
      }

      const latestPayment = payments[0];
      
      // Generate voucher from the latest settlement payment
      await createSettlementPaymentVoucher(latestPayment._id || latestPayment.paymentId);
      messageApi.success('Voucher generated successfully');
      await loadVouchers();
      onVoucherUpdate?.();
    } catch (error) {
      console.error('Generate voucher error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to generate voucher');
    } finally {
      setGeneratingVoucher(false);
    }
  };

  // Handle delete voucher
  const handleDeleteVoucher = async (voucherId) => {
    setDeletingVoucherId(voucherId);
    try {
      await deleteVoucher(voucherId);
      messageApi.success('Voucher deleted successfully');
      await loadVouchers();
      onVoucherUpdate?.();
    } catch (error) {
      console.error('Delete voucher error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to delete voucher');
    } finally {
      setDeletingVoucherId(null);
    }
  };

  // Handle quick actions
  const handleQuickPrint = (pdfUrl) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else {
      messageApi.warning('PDF URL not available');
    }
  };

  const handleQuickDownload = (pdfUrl, voucherId) => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `voucher-${voucherId}.pdf`;
      link.click();
    } else {
      messageApi.warning('PDF URL not available');
    }
  };

  // Format amount as whole rupee without decimals
  const formatWholeAmount = (price) =>
    new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    }).format(price || 0);

  // Table columns
  const columns = [
    {
      title: 'Voucher ID',
      dataIndex: 'voucherId',
      key: 'voucherId',
      width: 130,
      render: (id) => <Text strong>{id}</Text>,
      sorter: (a, b) => a.voucherId.localeCompare(b.voucherId),
    },
    {
      title: 'Payment ID',
      dataIndex: 'paymentId',
      key: 'paymentId',
      width: 130,
      ellipsis: true,
    },
    {
      title: 'Category',
      dataIndex: 'advanceCategory',
      key: 'advanceCategory',
      render: (category) => {
        const color = category === 'LOAN_PAYMENT' ? 'blue' : 'green';
        const label = category === 'LOAN_PAYMENT' ? 'Loan Payment' : 'Take-Home';
        return <Tag color={color}>{label}</Tag>;
      },
      width: 140,
      filters: [
        { text: 'Loan Payment', value: 'LOAN_PAYMENT' },
        { text: 'Customer Take-Home', value: 'CUSTOMER_TAKE_HOME' },
      ],
      onFilter: (value, record) => record.advanceCategory === value,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: '#3f8600' }}>
          {formatWholeAmount(amount)}
        </Text>
      ),
      width: 120,
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: 'UTR / Ref.',
      key: 'utr',
      width: 240,
      render: (_, record) => (
        <SplitReferenceCell record={record} splits={resolveSplits(record)} expectedTotal={record.amount} />
      ),
    },
    {
      title: 'Recipient',
      dataIndex: 'recipientName',
      key: 'recipientName',
      ellipsis: true,
      width: 140,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        let color = 'default';
        let text = status;
        
        if (status === 'ISSUED') {
          color = 'green';
        } else if (status === 'REGENERATED') {
          color = 'orange';
          text = `${status} (${record.regenerationHistory?.length || 0}x)`;
        } else if (status === 'VOIDED') {
          color = 'red';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
      width: 120,
      filters: [
        { text: 'Issued', value: 'ISSUED' },
        { text: 'Regenerated', value: 'REGENERATED' },
        { text: 'Voided', value: 'VOIDED' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Issued At',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      render: (date) => date ? dayjs(date).format('DD MMM YYYY') : '-',
      width: 120,
      sorter: (a, b) => new Date(a.issuedAt) - new Date(b.issuedAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewVoucher(record.voucherId)}
            />
          </Tooltip>
          
          <Tooltip title="Print PDF">
            <Button
              type="text"
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => handleQuickPrint(record.pdfUrl)}
              disabled={!record.pdfUrl}
            />
          </Tooltip>

          <Tooltip title="Download PDF">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleQuickDownload(record.pdfUrl, record.voucherId)}
              disabled={!record.pdfUrl}
            />
          </Tooltip>

          <Popconfirm
            title="Delete this voucher?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true, loading: deletingVoucherId === record.voucherId }}
            cancelText="Cancel"
            onConfirm={() => handleDeleteVoucher(record.voucherId)}
          >
            <Tooltip title="Delete Voucher">
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deletingVoucherId === record.voucherId}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      
      {/* Header */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              Payment Vouchers
              {vouchers.length > 0 && <Text type="secondary"> ({vouchers.length})</Text>}
            </Title>
            <Text type="secondary">All vouchers generated for this case</Text>
          </div>
          
          <Space>
            {vouchers.length > 0 && (
              <Text type="secondary">
                Total Amount: {formatWholeAmount(vouchers.reduce((sum, v) => sum + (v.amount || 0), 0))}
              </Text>
            )}
            {isWalkInCase && (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                loading={generatingVoucher}
                onClick={handleGenerateVoucher}
              >
                Generate Voucher
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      {/* Vouchers Table */}
      <Spin spinning={loading}>
        <Table
          dataSource={vouchers.map((v) => ({ ...v, key: v._id }))}
          columns={columns}
          pagination={vouchers.length > 10 ? { 
            pageSize: 10, 
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} vouchers`,
          } : false}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
          locale={{ 
            emptyText: (
              <Empty 
                description="No vouchers generated yet"
                style={{ padding: '40px 20px' }}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
        />
      </Spin>

      {/* Manual Voucher Generation for WalkIn Cases - Info Message */}
      {isWalkInCase && vouchers.length === 0 && !loading && (
        <Card
          size="small"
          style={{ marginTop: 16, backgroundColor: '#f6f8fb', borderColor: '#e6f7ff' }}
        >
          <Text type="secondary">
            💡 No vouchers generated yet. Click the <strong>"Generate Voucher"</strong> button above to create one from a settlement payment.
          </Text>
        </Card>
      )}

      {/* Voucher Details Modal */}
      <VoucherDetailsModal
        open={detailsModalOpen}
        onCancel={() => {
          setDetailsModalOpen(false);
          setSelectedVoucherId(null);
        }}
        voucherId={selectedVoucherId}
        onRegenerateSuccess={handleRegenerateSuccess}
      />
    </>
  );
};

export default VoucherTable;